/**
 * Un regime fiscale nuovo: IVA, ritenuta d'acconto e cassa previdenziale, ciascuna facoltativa («non per forza sarà
 * coinvolta l'Enasarco», Simone 19/10/2026). Parte dal caso più frequente in Italia: società di capitali.
 */

import React, { useEffect, useState } from 'react';
import {
  Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { creaRegime } from '../../services/partnerConsoleService';

interface Props {
  aperto: boolean;
  onChiudi: () => void;
  onSalvato: () => void;
  onErrore: (messaggio: string) => void;
}

interface Modulo {
  nome: string;
  paese: string;
  conIva: boolean;
  iva: string;
  conRitenuta: boolean;
  aliquota: string;
  base: string;
  conCassa: boolean;
  nomeCassa: string;
  quotaNostra: string;
  quotaPartner: string;
}

const INIZIALE: Modulo = {
  nome: '', paese: 'IT', conIva: true, iva: '22', conRitenuta: true, aliquota: '23', base: '50',
  conCassa: true, nomeCassa: 'Enasarco', quotaNostra: '3', quotaPartner: '1',
};

const numero = (v: string): number => Number(v.replace(',', '.'));
const percentuale = (v: string) => v.trim() !== '' && Number.isFinite(numero(v)) && numero(v) >= 0 && numero(v) <= 100;

export const DialogoRegime: React.FC<Props> = ({ aperto, onChiudi, onSalvato, onErrore }) => {
  const { t } = useTranslation('partner');
  const [m, setM] = useState<Modulo>(INIZIALE);
  const [lavora, setLavora] = useState(false);

  useEffect(() => { if (aperto) setM(INIZIALE); }, [aperto]);
  const set = <K extends keyof Modulo>(k: K, v: Modulo[K]) => setM((x) => ({ ...x, [k]: v }));

  const completo = m.nome.trim().length > 1 && /^[A-Za-z]{2}$/.test(m.paese)
    && (!m.conIva || percentuale(m.iva))
    && (!m.conRitenuta || (percentuale(m.aliquota) && percentuale(m.base)))
    && (!m.conCassa || (m.nomeCassa.trim().length > 0 && percentuale(m.quotaNostra) && percentuale(m.quotaPartner)));

  const salva = async () => {
    setLavora(true);
    try {
      await creaRegime({
        nome: m.nome.trim(),
        paese: m.paese.toUpperCase(),
        iva_percentuale: m.conIva ? numero(m.iva) : null,
        ritenuta: m.conRitenuta ? { aliquota: numero(m.aliquota), base_percentuale: numero(m.base) } : null,
        cassa: m.conCassa ? { nome: m.nomeCassa.trim(), quota_nostra: numero(m.quotaNostra), quota_partner: numero(m.quotaPartner) } : null,
      });
      onSalvato();
      onChiudi();
    } catch (e) {
      onErrore(e instanceof Error ? e.message : String(e));
    } finally {
      setLavora(false);
    }
  };

  const campo = (k: keyof Modulo, etichetta: string, larghezza = 140) => (
    <TextField label={etichetta} value={m[k] as string} onChange={(e) => set(k, e.target.value as never)} size="small" sx={{ width: larghezza }} inputProps={{ inputMode: 'decimal' }} />
  );
  const interruttore = (k: 'conIva' | 'conRitenuta' | 'conCassa', etichetta: string) => (
    <FormControlLabel control={<Checkbox checked={m[k]} onChange={(e) => set(k, e.target.checked)} size="small" />} label={etichetta} />
  );

  return (
    <Dialog open={aperto} onClose={lavora ? undefined : onChiudi} fullWidth maxWidth="sm">
      <DialogTitle>{t('dialogo_regime.titolo')}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1}>
            <TextField label={t('dialogo_regime.nome')} helperText={t('dialogo_regime.nome_aiuto')} value={m.nome} onChange={(e) => set('nome', e.target.value)} size="small" sx={{ flex: 1 }} />
            <TextField label={t('dialogo_regime.paese')} value={m.paese} onChange={(e) => set('paese', e.target.value)} size="small" sx={{ width: 90 }} inputProps={{ maxLength: 2 }} />
          </Stack>
          {interruttore('conIva', t('dialogo_regime.con_iva'))}
          {m.conIva && <Stack direction="row" spacing={1} sx={{ pl: 4 }}>{campo('iva', t('dialogo_regime.iva'))}</Stack>}
          {interruttore('conRitenuta', t('dialogo_regime.con_ritenuta'))}
          {m.conRitenuta && (
            <Stack direction="row" spacing={1} sx={{ pl: 4 }}>
              {campo('aliquota', t('dialogo_regime.aliquota'))}
              {campo('base', t('dialogo_regime.base'), 180)}
            </Stack>
          )}
          {interruttore('conCassa', t('dialogo_regime.con_cassa'))}
          {m.conCassa && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ pl: 4 }}>
              <TextField label={t('dialogo_regime.nome_cassa')} value={m.nomeCassa} onChange={(e) => set('nomeCassa', e.target.value)} size="small" sx={{ width: 160 }} />
              {campo('quotaNostra', t('dialogo_regime.quota_nostra'))}
              {campo('quotaPartner', t('dialogo_regime.quota_partner'), 160)}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onChiudi} disabled={lavora} sx={{ textTransform: 'none' }}>{t('azioni.annulla')}</Button>
        <Button variant="contained" onClick={salva} disabled={!completo || lavora} startIcon={lavora ? <CircularProgress size={14} /> : undefined} sx={{ textTransform: 'none' }}>
          {t('azioni.salva')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
